import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Plus, Search, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { useCustomers } from '@/features/admin-customers/api';
import type { Customer } from '@/features/admin-customers/types';

export function CustomersList() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const { data, isLoading, isError, error } = useCustomers(search);

  const columns: DataTableColumn<Customer>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (c) => (
        <div>
          <Link
            to={`/admin/customers/${c.id}`}
            className="font-medium hover:underline"
            data-testid={`admin-customer-list-row-name-${c.id}`}
          >
            {c.name}
          </Link>
          <div className="text-xs text-muted-foreground">
            <code>customer:{c.companySlug}</code>
          </div>
        </div>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      cell: (c) => <span className="text-muted-foreground">{c.email}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (c) =>
        c.isActive ? (
          <StatusBadge variant="success" label="Active" />
        ) : (
          <StatusBadge variant="neutral" label="Inactive" />
        ),
    },
    {
      id: 'scopes',
      header: 'Product scopes',
      cell: (c) => (
        <div className="flex flex-wrap gap-1">
          {c.productScopes.length === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            c.productScopes.map((s) => (
              <Badge key={s} variant="outline">
                {s}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: (c) => (
        <span className="text-muted-foreground">
          {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-customer-list-root">
      <PageHeader
        title="Customers"
        actions={
          <Button asChild>
            <Link to="/admin/customers/new">
              <Plus className="h-4 w-4" />
              New customer
            </Link>
          </Button>
        }
      />

      <div className="relative max-w-sm">
        <Search
          className="pointer-events-none absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          placeholder="Search name or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-8"
        />
      </div>

      <DataTable
        columns={columns}
        rows={data?.customers}
        getRowKey={(c) => c.id}
        isLoading={isLoading}
        isError={isError}
        errorState={
          <div className="text-center text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load customers'}
          </div>
        }
        emptyState={
          <EmptyState
            Icon={Users}
            title={search ? `No customers match "${search}"` : 'No customers yet'}
          />
        }
        rowProps={(c) => ({
          className: 'cursor-pointer',
          'data-testid': 'admin-customer-list-row',
          'data-customer-id': c.id,
          'data-customer-email': c.email,
        })}
      />
    </div>
  );
}
